/**
 * Procedural grain for the cockpit's interior surfaces.
 *
 * ## Why any of this
 *
 * Every surface in the cockpit was a flat colour with a single roughness value — no roughness map,
 * no normal map, no ambient occlusion, no environment reflection. A physically-based renderer
 * given a constant roughness and a constant albedo produces exactly what it is asked for: a
 * perfectly uniform surface, which does not exist and which the eye reads instantly as *plastic*.
 * It is the same reason an untextured sphere looks like a rendering and a scuffed one looks like
 * an object.
 *
 * Real cockpit surfaces are the opposite of uniform. Moulded vinyl has a coarse pebble grain that
 * catches light at glancing angles; a painted metal frame is smoother but scuffed along the edges
 * people touch; leather has a fine irregular break. None of that needs to be *resolved* — at
 * cockpit distances it is well under a pixel — but it has to be *there*, because what it changes
 * is the specular response across the surface, and that is what carries shape.
 *
 * ## Why procedural, and why tiny
 *
 * The initial payload budget is 6 MB gzipped for the entire product. A set of authored PBR maps
 * for an interior is comfortably more than that on its own. These are generated at start-up into
 * 256² canvases — about 0.8 ms each, once — and cached for the session, so they cost nothing to
 * download and nothing to keep.
 *
 * The noise is value noise summed over four octaves rather than anything cleverer, for the good
 * reason that the output is a *roughness perturbation of ±0.12*. Nobody can tell which noise
 * function generated a variation that small; they can very easily tell there is none.
 *
 * ## The normal map is derived, not drawn
 *
 * Its height field is the same noise as the roughness map, so the bumps and the dull patches are
 * the same bumps — which is what makes a surface look like one material rather than two effects
 * layered on it. The gradient is a central difference, and the `z` component is a constant that
 * sets the strength: a small `z` is a violent surface, and 1 is nearly flat.
 *
 * `updateFixed` / `updateFrame`: neither. Built once, on demand.
 */

import { CanvasTexture, LinearFilter, RepeatWrapping } from 'three';

/** How large each generated map is. 256² is 0.26 MB uploaded and more than the eye can use here. */
const SIZE = 256;

export type GrainKind = 'vinyl' | 'leather' | 'metal';

/** How the three surfaces differ: feature size, contrast, and how deep the relief reads. */
const GRAIN: Readonly<Record<GrainKind, { cells: number; contrast: number; relief: number }>> =
  Object.freeze({
    // Moulded vinyl: a coarse, regular pebble.
    vinyl: { cells: 26, contrast: 0.55, relief: 2.2 },
    // Leather: larger cells with more contrast between them, and a softer break.
    leather: { cells: 15, contrast: 0.7, relief: 1.5 },
    // Painted metal: nearly smooth, with fine directional scuffing.
    metal: { cells: 40, contrast: 0.3, relief: 5 },
  });

const roughnessCache = new Map<GrainKind, CanvasTexture>();
const normalCache = new Map<GrainKind, CanvasTexture>();

/**
 * Deterministic value noise on a torus.
 *
 * Wrapping is not a nicety — these textures tile across a cabin wall, and a seam down the middle
 * of a sidewall is far more visible than the grain itself. Seeded from the kind so the same
 * surface is the same surface between sessions, and so nothing here reaches for `Math.random`,
 * which non-negotiable #7 routes through one seeded generator anyway.
 */
function valueNoise(kind: GrainKind): Float32Array {
  const { cells, contrast } = GRAIN[kind];
  const field = new Float32Array(SIZE * SIZE);

  // A tiny xorshift, seeded off the kind's name, so the lattice is stable and cheap.
  let state = 0x9e3779b9;
  for (const character of kind) state = (state ^ character.charCodeAt(0)) * 0x01000193;
  const random = (): number => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) % 65_536) / 65_536;
  };

  let amplitude = 1;
  let total = 0;
  for (let octave = 0; octave < 4; octave++) {
    const size = Math.max(2, Math.round(cells * 2 ** octave));
    const lattice = new Float32Array(size * size);
    for (let i = 0; i < lattice.length; i++) lattice[i] = random();

    for (let y = 0; y < SIZE; y++) {
      const fy = (y / SIZE) * size;
      const y0 = Math.floor(fy) % size;
      const y1 = (y0 + 1) % size;
      const ty = fy - Math.floor(fy);
      // Smoothstep, so the lattice does not show as a diamond grid.
      const wy = ty * ty * (3 - 2 * ty);
      for (let x = 0; x < SIZE; x++) {
        const fx = (x / SIZE) * size;
        const x0 = Math.floor(fx) % size;
        const x1 = (x0 + 1) % size;
        const tx = fx - Math.floor(fx);
        const wx = tx * tx * (3 - 2 * tx);
        const top = lattice[y0 * size + x0] * (1 - wx) + lattice[y0 * size + x1] * wx;
        const bottom = lattice[y1 * size + x0] * (1 - wx) + lattice[y1 * size + x1] * wx;
        field[y * SIZE + x] += (top * (1 - wy) + bottom * wy) * amplitude;
      }
    }
    total += amplitude;
    amplitude *= 0.5;
  }

  // Normalise to 0…1, then pull toward the middle by `contrast` so a low-contrast surface is a
  // gentle mottle rather than a faded version of a violent one.
  for (let i = 0; i < field.length; i++) {
    const unit = field[i] / total;
    field[i] = 0.5 + (unit - 0.5) * contrast;
  }
  return field;
}

function makeCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('2D canvas context unavailable — cockpit surfaces cannot be generated');
  }
  return { canvas, ctx };
}

function finish(canvas: HTMLCanvasElement, repeat: number): CanvasTexture {
  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(repeat, repeat);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  // Left in linear space deliberately: both maps are *data*, and running a roughness value or a
  // packed normal through an sRGB transfer function is the classic way to get a surface that is
  // subtly, inexplicably wrong everywhere.
  texture.needsUpdate = true;
  return texture;
}

/** A roughness map for this surface — mid-grey with the grain written into it. */
export function grainRoughness(kind: GrainKind, repeat = 4): CanvasTexture {
  const cached = roughnessCache.get(kind);
  if (cached !== undefined) return cached;

  const field = valueNoise(kind);
  const { canvas, ctx } = makeCanvas();
  const image = ctx.createImageData(SIZE, SIZE);
  for (let i = 0; i < field.length; i++) {
    // The material's own `roughness` multiplies this, so the map is centred on 1 and varies
    // around it rather than scaling the surface toward mirror-smooth.
    const value = Math.round(clamp01(0.62 + field[i] * 0.55) * 255);
    image.data[i * 4] = value;
    image.data[i * 4 + 1] = value;
    image.data[i * 4 + 2] = value;
    image.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);

  const texture = finish(canvas, repeat);
  roughnessCache.set(kind, texture);
  return texture;
}

/** A tangent-space normal map derived from the same noise as {@link grainRoughness}. */
export function grainNormal(kind: GrainKind, repeat = 4): CanvasTexture {
  const cached = normalCache.get(kind);
  if (cached !== undefined) return cached;

  const field = valueNoise(kind);
  const relief = GRAIN[kind].relief;
  const { canvas, ctx } = makeCanvas();
  const image = ctx.createImageData(SIZE, SIZE);

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      // Central differences, wrapped — the height field is a torus and so is its gradient.
      const left = field[y * SIZE + ((x - 1 + SIZE) % SIZE)];
      const right = field[y * SIZE + ((x + 1) % SIZE)];
      const up = field[((y - 1 + SIZE) % SIZE) * SIZE + x];
      const down = field[((y + 1) % SIZE) * SIZE + x];

      const dx = (left - right) * 4;
      const dy = (up - down) * 4;
      const length = Math.hypot(dx, dy, relief);
      const index = (y * SIZE + x) * 4;
      image.data[index] = Math.round((dx / length * 0.5 + 0.5) * 255);
      image.data[index + 1] = Math.round((dy / length * 0.5 + 0.5) * 255);
      image.data[index + 2] = Math.round((relief / length * 0.5 + 0.5) * 255);
      image.data[index + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);

  const texture = finish(canvas, repeat);
  normalCache.set(kind, texture);
  return texture;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Drop every cached map. For tests and for a full renderer teardown. */
export function disposeCockpitSurfaces(): void {
  for (const texture of roughnessCache.values()) texture.dispose();
  for (const texture of normalCache.values()) texture.dispose();
  roughnessCache.clear();
  normalCache.clear();
}
